import { useEffect, useState } from "react";

import { Coding } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { usePracticeId } from "@/hooks/usePracticeId";

import { DEFAULT_SERVICE_DATE_TIMEZONE } from "../constants/service-date-timezone";
import { UsePracticeTimezoneOptions } from "../interfaces/service-date-timezone";
import { fetchDefaultServiceDateTimezone } from "../services/practice-timezone";

/**
 * Loads the default IANA timezone for service date inputs.
 *
 * With `usePracticeDefault`, the practice default timezone wins and sets `isPracticeConfigured`.
 * Without it the practice default is not read, so a failed Organization read cannot affect the caller.
 * A practice whose location hours configure one distinct timezone uses it and sets
 * `isPracticeConfigured`. Zero or several distinct timezones use the browser timezone and leave
 * `isPracticeConfigured` false, so a caller that needs one timezone for every viewer can hold off.
 *
 * A failed lookup keeps the default timezone and sets `hasError`, so a caller that must not act on
 * a guessed timezone can hold off.
 *
 * `isLoading` stays true until the result belongs to the current practice. After a practice switch
 * the previous practice's timezone is still in state for a render, and this keeps callers from
 * acting on it.
 *
 * @param options Set `usePracticeDefault` for a caller that needs the practice default (billing).
 * @returns Default timezone coding, whether it came from the practice configuration, whether the
 * value is still loading, and whether the lookup failed.
 */
export function usePracticeTimezone({ usePracticeDefault }: UsePracticeTimezoneOptions = {}) {
  const medplum = useMedplum();
  const practiceId = usePracticeId();
  const [timezone, setTimezone] = useState<Coding>({ ...DEFAULT_SERVICE_DATE_TIMEZONE });
  const [isPracticeConfigured, setIsPracticeConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [loadedPracticeId, setLoadedPracticeId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!practiceId) {
      setTimezone({ ...DEFAULT_SERVICE_DATE_TIMEZONE });
      setIsPracticeConfigured(false);
      setHasError(false);
      setLoadedPracticeId(undefined);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const loadTimezone = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const resolution = await fetchDefaultServiceDateTimezone(medplum, practiceId, {
          usePracticeDefault,
        });
        if (!isCancelled) {
          setTimezone(resolution.timezone);
          setIsPracticeConfigured(resolution.isPracticeConfigured);
        }
      } catch (error) {
        Sentry.captureException(error);
        if (!isCancelled) {
          setTimezone({ ...DEFAULT_SERVICE_DATE_TIMEZONE });
          setIsPracticeConfigured(false);
          setHasError(true);
        }
      } finally {
        if (!isCancelled) {
          setLoadedPracticeId(practiceId);
          setIsLoading(false);
        }
      }
    };

    void loadTimezone();

    return () => {
      isCancelled = true;
    };
  }, [medplum, practiceId, usePracticeDefault]);

  return {
    timezone,
    isPracticeConfigured,
    isLoading: isLoading || loadedPracticeId !== practiceId,
    hasError,
  };
}
